import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Devices as DevicesIcon,
  LocationOn as LocationIcon,
  Call as CallIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';

function Dashboard() {
  const [stats, setStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    recentCalls: 0,
    unreadAlerts: 0
  });
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    
    // Subscribe to real-time updates
    const devicesSubscription = supabase
      .channel('devices_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'devices' 
      }, () => {
        loadDashboardData();
      })
      .subscribe();

    const alertsSubscription = supabase
      .channel('alerts_changes')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'alerts' 
      }, (payload) => {
        setRecentAlerts(prev => [payload.new, ...prev].slice(0, 5));
      })
      .subscribe();

    return () => {
      devicesSubscription.unsubscribe();
      alertsSubscription.unsubscribe();
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      // Load devices
      const { data: devicesData } = await supabase
        .from('devices')
        .select('*')
        .eq('user_id', user.id);

      setDevices(devicesData || []);

      // Load recent calls (last 24 hours)
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data: callsData } = await supabase
        .from('call_logs')
        .select('id', { count: 'exact', head: true })
        .in('device_id', (devicesData || []).map(d => d.id))
        .gte('start_time', yesterday);

      // Load unread alerts
      const { data: alertsData } = await supabase
        .from('alerts')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(5);

      setRecentAlerts(alertsData || []);

      setStats({
        totalDevices: devicesData?.length || 0,
        onlineDevices: devicesData?.filter(d => d.status === 'online').length || 0,
        recentCalls: callsData?.length || 0,
        unreadAlerts: alertsData?.length || 0
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4">
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Devices"
            value={stats.totalDevices}
            icon={<DevicesIcon sx={{ color: 'white' }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Online Now"
            value={stats.onlineDevices}
            icon={<LocationIcon sx={{ color: 'white' }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Calls (24h)"
            value={stats.recentCalls}
            icon={<CallIcon sx={{ color: 'white' }} />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unread Alerts"
            value={stats.unreadAlerts}
            icon={<WarningIcon sx={{ color: 'white' }} />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Devices
            </Typography>
            <List>
              {devices.length === 0 ? (
                <ListItem>
                  <ListItemText 
                    primary="No devices registered"
                    secondary="Install the mobile app to get started"
                  />
                </ListItem>
              ) : (
                devices.map((device) => (
                  <ListItem key={device.id}>
                    <ListItemText
                      primary={device.device_name}
                      secondary={`${device.device_type} - ${device.os_version}`}
                    />
                    <Chip
                      label={device.status}
                      color={device.status === 'online' ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Alerts
            </Typography>
            <List>
              {recentAlerts.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No recent alerts" />
                </ListItem>
              ) : (
                recentAlerts.map((alert) => (
                  <ListItem key={alert.id}>
                    <ListItemText
                      primary={alert.title}
                      secondary={alert.message}
                    />
                    <Chip
                      label={alert.severity}
                      color={
                        alert.severity === 'critical' ? 'error' :
                        alert.severity === 'warning' ? 'warning' : 'default'
                      }
                      size="small"
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
